import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Col, DatePicker, Form, Input, Row } from 'antd'

import A11yButton from '@/components/A11yButton'
import B2becContactVia from '@/components/B2becContactVia'
import B2becEnquiryModal from '@/components/B2becEnquiryModal'
import B2becTranslation from '@/components/B2becTranslation'
import B2BecLink from '@/components/B2BLink'
import CustomButton from '@/components/CustomButton'
import { NotificationManager } from '@/components/Notification'
import { rentalService } from '@/services/fapi'
import { useAppSelector } from '@/store/hook'
import { selectCountryNames, selectRegions } from '@/store/selectors/configSelector'
import { selectCustomerAddress } from '@/store/slices/customerSlice'
import { useAsync } from '@/hooks'
import { dayjs, getFormatType } from '@/libs/utils/dateUtils'

import CustomAddressSelect from './CustomSelectAddress'

const { useForm } = Form

const RentalModal = (props) => {
  const { children, deviceName } = props
  const { t } = useTranslation()
  const customerAddress = useAppSelector(selectCustomerAddress)
  const regions = useAppSelector(selectRegions)
  const companyName = customerAddress?.name
  const [form] = useForm()

  const [visible, setVisible] = useState(false)

  const countryNamesConfig = useAppSelector(selectCountryNames)
  const countryName = useMemo(() => {
    if (!customerAddress) {
      return ''
    }

    const foundCountryName = countryNamesConfig.find(
      (countryNameConfig) => countryNameConfig.value === customerAddress.country,
    )

    return foundCountryName ? foundCountryName.name : customerAddress.country
  }, [countryNamesConfig, customerAddress])

  const { execute, status } = useAsync(
    async (values) => {
      return rentalService.requestRental(values)
    },
    false,
    () => {
      setVisible(false)
      NotificationManager.success({
        message: 'rental.successfullMessage',
      })
      form.resetFields()
    },
  )

  const handleSubmit = useCallback(async () => {
    const values = await form.validateFields()

    const body = {
      deviceName: values.deviceName,
      cleanObject: values.cleanObject,
      spaceMeasurement: values.spaceMeasurement,
      startDate: values.startDate.format(getFormatType().default),
      duration: values.duration,
      phoneNumber: values.contactPhone,
      email: values.contactEmail,
      furtherInformation: values.furtherInformation,
      firstName: values.firstName,
      lastName: values.lastName,
      companyName: values.companyName,
      street: values.address.street,
      street4: values.address.street4,
      number: values.address.houseNumber || undefined,
      postCode: values.address.zip,
      region: values.address.region,
      country: customerAddress.country,
      city: values.address.city,
    }
    if (body.region && regions[body.region]) {
      body.region = regions[body.region].label
    }
    execute(body)
  }, [customerAddress.country, execute, form, regions])

  const isSubmitting = status === 'pending'

  return (
    <div>
      <A11yButton elementType="div" onClick={() => setVisible(true)}>
        {children}
      </A11yButton>
      {visible && (
        <B2becEnquiryModal>
          <div className="enquiry-modal">
            <h3 className="enquiry-modal__header">{t('rental.modal.title')}</h3>
            <div className="scrollable">
              <p className="enquiry-modal__description">
                {t('rental.modal.description')}
              </p>
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  companyName,
                  deviceName,
                  country: countryName,
                }}
                validateMessages={{
                  required: t('myProfile.form.validateMessages.required'),
                  types: {
                    email: t('enquiryModal.form.validateMesages.email'),
                    phone: t('enquiryModal.form.validateMesages.phone'),
                    number: t('enquiryModal.form.validateMesages.number'),
                  },
                }}
              >
                <Row gutter={12}>
                  <Col span={24}>
                    <Form.Item
                      className="custom-form-item"
                      label="Device"
                      name="deviceName"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('rental.modal.cleanObject')}
                      name="cleanObject"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('rental.modal.spaceMeasurement')}
                      name="spaceMeasurement"
                      rules={[
                        {
                          pattern: /^\d+$/,
                          message: t(
                            'enquiryModal.form.validateMesages.number',
                          ),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('rental.modal.startDate')}
                      name="startDate"
                      rules={[{ required: true }]}
                    >
                      <DatePicker
                        format={getFormatType().default}
                        showNow={false}
                        style={{ width: '100%' }}
                        disabledDate={(current) => current?.isBefore(dayjs(), 'day')}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('rental.modal.duration')}
                      name="duration"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('rental.modal.furtherInformation')}
                      name="furtherInformation"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col>
                    <h4 className="mb-2">
                      {t('service.serviceOverview.label.contactInformation')}
                    </h4>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('enquiryModal.form.companyName.label')}
                      name="companyName"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('form.label.contactPersonFirstName')}
                      name="firstName"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('form.label.contactPersonLastName')}
                      name="lastName"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      className="custom-form-item"
                      label={t('rental.modal.locationMachine')}
                      name="address"
                      rules={[{ required: true }]}
                    >
                      <CustomAddressSelect />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      required
                      className="custom-form-item"
                      label={t('enquiryModal.form.contactType.label')}
                    >
                      <B2becContactVia />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      className="custom-form-item"
                      name="term"
                      valuePropName="checked"
                      rules={[
                        () => ({
                          validator(_, value) {
                            if (value === true) {
                              return Promise.resolve()
                            }
                            return Promise.reject(
                              t('rental.modal.acceptTermsErrorMessage'),
                            )
                          },
                        }),
                      ]}
                    >
                      <Checkbox className="kaercher-checkbox">
                        <span className="mandatory-title">
                          <B2becTranslation
                            isComponent
                            value="rental.modal.acceptTerms"
                            htmlTags={[
                              <B2BecLink
                                key="AGB"
                                target="_blank"
                                to="/data-protection-policy"
                              >
                                {/* Link */}
                              </B2BecLink>,
                            ]}
                          />
                        </span>
                      </Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
            <B2becEnquiryModal.Footer>
              <CustomButton
                disabled={isSubmitting}
                onClick={() => setVisible(false)}
                type="ghost"
              >
                {t('enquiryModal.button.cancel')}
              </CustomButton>
              <CustomButton disabled={isSubmitting} onClick={handleSubmit}>
                {t('enquiryModal.button.submitForm')}
              </CustomButton>
            </B2becEnquiryModal.Footer>
          </div>
        </B2becEnquiryModal>
      )}
    </div>
  )
}

export default RentalModal
